import mixpanel from 'mixpanel-browser';

import { config } from './config';
import type { MixpanelContextValue } from './mixpanelContext';
import { isMixpanelSuppressed } from './mixpanelSuppression';

const token = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;
const apiHost = import.meta.env.VITE_MIXPANEL_HOST as string | undefined;

// Initialised at import rather than in an effect: React runs child effects before the provider's, so anything a
// page tracks on mount would otherwise reach an uninitialised client and be dropped.
if (token) {
    mixpanel.init(token, {
        persistence: 'localStorage',
        track_pageview: false,
        debug: config.isDevMode,
        batch_flush_interval_ms: 1000,
        ip: false,
        // URLs the SDK attaches on its own carry checkout tokens and OTPs, which grant access. Page views send the
        // route pattern instead.
        property_blacklist: [
            '$current_url',
            '$referrer',
            '$initial_referrer',
            'current_url_path',
            'current_url_search',
        ],
        ...(apiHost ? { api_host: apiHost } : {}),
    });
    // Browsers that opened the cockpit while tracking was opted out by default still carry that opt-out in
    // localStorage, which would keep them untracked. There is no opt-out of our own to preserve.
    if (mixpanel.has_opted_out_tracking()) {
        mixpanel.clear_opt_in_out_tracking();
    }
}

function createDevMock(): MixpanelContextValue {
    // Mirrors the SDK merging its registered properties into every event, so the console shows what would be sent.
    let eventProperties: Record<string, string> = {};
    let storedUserId: string | undefined;
    const log = (label: string, payload: unknown): void => {
        // oxlint-disable-next-line no-console
        console.info(`[Mixpanel]${isMixpanelSuppressed() ? ' (suppressed)' : ''}`, label, payload ?? '');
    };
    return {
        track: (event, properties) => log(event, { ...eventProperties, ...properties }),
        trackPageView: (properties) => log('page_view', { ...eventProperties, ...properties }),
        identify: (userId) => {
            storedUserId = userId;
            log('identify', userId);
        },
        setUserProperties: (properties) => log('people.set', properties),
        setEventProperties: (properties) => {
            eventProperties = withEventProperties(eventProperties, properties);
        },
        reset: () => {
            eventProperties = {};
            storedUserId = undefined;
            log('reset', undefined);
        },
        storedUserId: () => storedUserId,
    };
}

function withEventProperties(
    current: Record<string, string>,
    changes: Record<string, string | undefined>,
): Record<string, string> {
    const next = { ...current };
    for (const [name, value] of Object.entries(changes)) {
        if (value === undefined) {
            delete next[name];
        } else {
            next[name] = value;
        }
    }
    return next;
}

function createRealMixpanel(): MixpanelContextValue {
    return {
        track: (event, properties) => {
            if (isMixpanelSuppressed()) {
                return;
            }
            mixpanel.track(event, properties);
        },
        trackPageView: (properties) => {
            if (isMixpanelSuppressed()) {
                return;
            }
            mixpanel.track_pageview(properties);
        },
        identify: (userId) => {
            mixpanel.identify(userId);
        },
        setUserProperties: (properties) => {
            mixpanel.people.set(properties);
        },
        setEventProperties: (properties) => {
            for (const [name, value] of Object.entries(properties)) {
                if (value === undefined) {
                    mixpanel.unregister(name);
                } else {
                    mixpanel.register({ [name]: value });
                }
            }
        },
        // Also clears the registered event properties, so the next user starts without the previous partner.
        reset: () => {
            mixpanel.reset();
        },
        storedUserId: () => {
            const stored: unknown = mixpanel.get_property('$user_id');
            return typeof stored === 'string' ? stored : undefined;
        },
    };
}

// One client for the whole app, so code outside React (route guards) tracks through the same path as components.
export const mixpanelClient: MixpanelContextValue = token ? createRealMixpanel() : createDevMock();
